import { Controller } from "@hotwired/stimulus"

// Project Settings — the header clock icon. Opens a modal listing every
// settings change: who, when, and each field's old → new value.
export default class extends Controller {
  static targets = ["modal", "body", "empty", "loading"]
  static values = { url: String }

  open() {
    this.modalTarget.classList.remove("hidden")
    this._load()
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) this.close()
  }

  stop(event) {
    event.stopPropagation()
  }

  async _load() {
    this.bodyTarget.innerHTML = ""
    this.emptyTarget.classList.add("hidden")
    this.loadingTarget.classList.remove("hidden")

    try {
      const resp = await fetch(this.urlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!resp.ok) throw new Error("failed")
      const data = await resp.json()
      const entries = data.entries || []
      this.emptyTarget.classList.toggle("hidden", entries.length > 0)

      entries.forEach(entry => {
        const wrap = document.createElement("div")
        wrap.className = "rounded-lg border border-gray-200 overflow-hidden"

        // People events read as a sentence ("Added Jane Doe as Architect");
        // settings events stay a from → to list (Phelipe 2026-08-27).
        const summary = entry.summary
          ? `<div class="flex items-start gap-2 px-3 py-2">
               <span class="mt-0.5 shrink-0">${this._badge(entry.icon)}</span>
               <span class="min-w-0">
                 <span class="block text-[13px] text-gray-800">${this._esc(entry.summary)}</span>
                 ${entry.detail ? `<span class="block text-[11px] text-gray-400">${this._esc(entry.detail)}</span>` : ""}
               </span>
             </div>`
          : ""

        const rows = (entry.changes || []).map(c => `
          <div class="flex items-baseline gap-2 px-3 py-1.5 text-[13px]">
            <span class="text-gray-500 shrink-0">${this._esc(c.field)}</span>
            <span class="flex-1 border-b border-dotted border-gray-200"></span>
            <span class="text-gray-400 break-all">${this._esc(c.from)}</span>
            <span class="text-gray-300 shrink-0">→</span>
            <span class="font-medium text-gray-900 break-all">${this._esc(c.to)}</span>
          </div>`).join("")

        wrap.innerHTML = `
          <div class="flex items-center justify-between px-3 py-1.5 bg-gray-50 text-[13px]">
            <span class="font-medium text-gray-800">${this._esc(entry.user)}</span>
            <span class="text-gray-400">${this._esc(entry.on)}</span>
          </div>
          ${summary}
          <div class="divide-y divide-gray-100">${rows}</div>`
        this.bodyTarget.appendChild(wrap)
      })
    } catch (e) {
      console.error("[project-settings-history] load failed:", e)
      this.emptyTarget.classList.remove("hidden")
    } finally {
      this.loadingTarget.classList.add("hidden")
    }
  }

  // Green + for joins, red − for removals, gray ± for a role change.
  _badge(kind) {
    const map = {
      add: ["bg-green-100 text-green-700", "+"],
      remove: ["bg-red-100 text-red-700", "\u2212"],
      change: ["bg-gray-100 text-gray-600", "\u21c4"]
    }
    const [cls, glyph] = map[kind] || map.change
    return `<span class="inline-flex items-center justify-center w-4 h-4 rounded text-[11px] font-semibold ${cls}">${glyph}</span>`
  }

  _esc(s) {
    const d = document.createElement("div")
    d.textContent = s == null ? "" : String(s)
    return d.innerHTML
  }
}
